import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import employeeService from '../../../services/employeeService';
import NotificationToast from '../../../components/ui/NotificationToast';
import apiClient from '../../../lib/apiClient';

const LeaveManagement = ({ isAdmin, clientId }) => {
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [notification, setNotification] = useState(null);
  const [filters, setFilters] = useState({
    employee_id: '',
    leave_type: '',
    status: '',
    date_from: '',
    date_to: ''
  });
  const [formData, setFormData] = useState({
    employee_id: '',
    leave_date: '',
    leave_type: 'unpaid',
    reason: '',
    status: 'pending'
  });

  useEffect(() => {
    loadEmployees();
    loadLeaves();
  }, [clientId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadLeaves();
    }, 500);
    return () => clearTimeout(timer);
  }, [filters, clientId]);

  const loadEmployees = async () => {
    try {
      const employeeFilters = {};
      if (clientId) {
        employeeFilters.client_id = clientId;
      }
      const result = await employeeService.getEmployees(employeeFilters);
      if (result?.success) {
        setEmployees(result.data || []);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadLeaves = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.employee_id) params.append('employee_id', filters.employee_id);
      if (filters.leave_type) params.append('leave_type', filters.leave_type);
      if (filters.status) params.append('status', filters.status);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (clientId) params.append('client_id', clientId);

      const result = await apiClient.get(`/leaves?${params.toString()}`);
      if (result?.success) {
        setLeaves(result.data || []);
      }
    } catch (error) {
      setNotification({ message: 'Failed to load leave records', type: 'error' });
      console.error('Error loading leaves:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.employee_id || !formData.leave_date) {
      setNotification({ message: 'Please fill all required fields', type: 'error' });
      return;
    }

    setLoading(true);
    try {
      let result;
      if (editingId) {
        result = await apiClient.put(`/leaves/${editingId}`, formData);
      } else {
        result = await apiClient.post('/leaves', formData);
      }

      if (result?.success) {
        setNotification({ 
          message: editingId ? 'Leave updated successfully' : 'Leave created successfully', 
          type: 'success' 
        });
        setShowForm(false);
        setEditingId(null);
        resetForm();
        loadLeaves();
      } else {
        setNotification({ message: result?.error || 'Failed to save leave', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred while saving leave', type: 'error' });
      console.error('Error saving leave:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this leave record?')) return;

    setLoading(true);
    try {
      const result = await apiClient.delete(`/leaves/${id}`);
      if (result?.success) {
        setNotification({ message: 'Leave deleted successfully', type: 'success' });
        loadLeaves();
      } else {
        setNotification({ message: result?.error || 'Failed to delete leave', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred while deleting leave', type: 'error' });
      console.error('Error deleting leave:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (leave) => {
    setFormData({
      employee_id: leave.employee_id,  // UUID FK from leave_records (correct)
      leave_date: leave.leave_date,
      end_date: leave.end_date || leave.leave_date,
      leave_type: leave.leave_type,
      reason: leave.reason || '',
      status: leave.status
    });
    setEditingId(leave.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      leave_date: '',
      end_date: '',
      leave_type: 'unpaid',
      reason: '',
      status: 'pending'
    });
    setEditingId(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'rejected': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getLeaveTypeColor = (type) => {
    switch (type) {
      case 'paid': return 'bg-blue-100 text-blue-800';
      case 'unpaid': return 'bg-gray-100 text-gray-800';
      case 'sick': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      <NotificationToast
        message={notification?.message}
        type={notification?.type}
        onDismiss={() => setNotification(null)}
      />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground">Leave Management</h4>
          <p className="text-sm text-muted-foreground">Manage employee leave records</p>
        </div>
        {isAdmin && (
          <Button onClick={() => { resetForm(); setShowForm(true); }} className="flex items-center gap-2">
            <Icon name="Plus" size={16} />
            Add Leave
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Employee</label>
            <Select
              value={filters.employee_id}
              onValueChange={(value) => setFilters(prev => ({ ...prev, employee_id: value }))}
              placeholder="All Employees"
              options={[
                { value: '', label: 'All Employees' },
                ...(employees.map(emp => ({
                  value: emp.id,
                  label: `${emp.full_name} (${emp.employee_id})`
                })) || [])
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Leave Type</label>
            <Select
              value={filters.leave_type}
              onValueChange={(value) => setFilters(prev => ({ ...prev, leave_type: value }))}
              placeholder="All Types"
              options={[
                { value: '', label: 'All Types' },
                { value: 'paid', label: 'Paid' },
                { value: 'unpaid', label: 'Unpaid' },
                { value: 'sick', label: 'Sick' },
                { value: 'casual', label: 'Casual' },
                { value: 'earned', label: 'Earned' }
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Status</label>
            <Select
              value={filters.status}
              onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              placeholder="All Statuses"
              options={[
                { value: '', label: 'All Statuses' },
                { value: 'pending', label: 'Pending' },
                { value: 'approved', label: 'Approved' },
                { value: 'rejected', label: 'Rejected' }
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Date From</label>
            <Input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Date To</label>
            <Input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
            />
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && isAdmin && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">
                  {editingId ? 'Edit Leave' : 'Add New Leave'}
                </h3>
                <button
                  onClick={() => { setShowForm(false); resetForm(); }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Employee *</label>
                <Select
                  value={formData.employee_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, employee_id: value }))}
                  placeholder="Select Employee"
                  options={[
                    { value: '', label: 'Select Employee' },
                    ...(employees.map(emp => ({
                      value: emp.id,
                      label: `${emp.full_name} (${emp.employee_id})`
                    })) || [])
                  ]}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Leave Start Date *</label>
                  <Input
                    type="date"
                    value={formData.leave_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, leave_date: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Leave End Date</label>
                  <Input
                    type="date"
                    value={formData.end_date || formData.leave_date}
                    min={formData.leave_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Leave empty for single-day leave</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Leave Type *</label>
                <Select
                  value={formData.leave_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, leave_type: value }))}
                  options={[
                    { value: 'paid', label: 'Paid Leave' },
                    { value: 'unpaid', label: 'Unpaid Leave' },
                    { value: 'sick', label: 'Sick Leave' },
                    { value: 'casual', label: 'Casual Leave' },
                    { value: 'earned', label: 'Earned Leave' }
                  ]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Reason</label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Enter reason for leave"
                />
              </div>
              {isAdmin && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Status *</label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                    options={[
                      { value: 'pending', label: 'Pending' },
                      { value: 'approved', label: 'Approved' },
                      { value: 'rejected', label: 'Rejected' }
                    ]}
                  />
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={loading}>
                  {loading ? 'Saving...' : 'Save Leave'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leaves Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading leave records...</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Employee</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Leave Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Reason</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                {isAdmin && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {leaves.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 6 : 5} className="px-4 py-8 text-center text-muted-foreground">
                    No leave records found
                  </td>
                </tr>
              ) : (
                leaves.map(leave => (
                  <tr key={leave.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">
                      <div>{new Date(leave.leave_date).toLocaleDateString()}</div>
                      {leave.end_date && leave.end_date !== leave.leave_date && (
                        <div className="text-xs text-muted-foreground">
                          → {new Date(leave.end_date).toLocaleDateString()}
                          {leave.total_days > 1 && ` (${leave.total_days} days)`}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-foreground">
                      {leave.full_name || leave.employee_name || 'N/A'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getLeaveTypeColor(leave.leave_type)}`}>
                        {leave.leave_type?.toUpperCase() || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {leave.reason || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(leave.status)}`}>
                        {leave.status?.toUpperCase() || 'N/A'}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(leave)}
                            className="text-primary hover:text-primary/80"
                            title="Edit"
                          >
                            <Icon name="Edit" size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(leave.id)}
                            className="text-destructive hover:text-destructive/80"
                            title="Delete"
                          >
                            <Icon name="Trash2" size={16} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LeaveManagement;

